import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Box from '@mui/material/Box';
import { type SxProps, type Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { type ChangeEvent, forwardRef, type InputHTMLAttributes, useState } from 'react';

import { Button } from '../Button/Button';
import { HiddenInput } from '../HiddenInput/HiddenInput';

type FilePickerProps = InputHTMLAttributes<HTMLInputElement> & {
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  accept?: string;
  label?: string;
  showSelectedFileName?: boolean;
  error?: string | null;
  icon?: React.ReactNode;
  buttonProps?: React.ComponentProps<typeof Button>;
  multiple?: boolean;
  hide?: boolean;
  sx?: SxProps<Theme>;
};

export const FilePicker = forwardRef(function FilePicker(
  {
    onChange,
    accept,
    error,
    multiple,
    label = 'Pick a file',
    showSelectedFileName = false,
    icon,
    buttonProps,
    hide = false,
    sx,
    ...rest
  }: FilePickerProps,
  ref: React.Ref<HTMLInputElement>
) {
  const [filename, setFilename] = useState<string | null>(null);
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file?.name && showSelectedFileName) {
      setFilename(file?.name);
    }
    onChange(event);
  };

  const boxSx: SxProps<Theme> = [
    ...(hide ? [{ display: 'none' }] : []),
    ...(buttonProps?.fullWidth ? [{ width: '100%' }] : []),
    ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
  ];
  const startIcon = icon ? icon : <CloudUploadIcon />;

  return (
    <Box sx={boxSx}>
      <Button
        component="label"
        role={undefined}
        size="medium"
        fullWidth={false}
        tabIndex={-1}
        startIcon={startIcon}
        {...buttonProps}
      >
        {label}
        <HiddenInput multiple={multiple} ref={ref} type="file" accept={accept} onChange={handleFileChange} {...rest} />
      </Button>
      {error ? (
        <Typography variant="body2" sx={{ color: 'error.main' }}>
          {error}
        </Typography>
      ) : null}
      {showSelectedFileName && filename ? (
        <Typography variant="body2" sx={{ color: 'text.secondary', display: 'inline-block', ml: 2 }}>
          {filename}
        </Typography>
      ) : null}
    </Box>
  );
});
